import { Upload } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';

import { ALLOWED_MEDIA_TYPES, allowedTypesFor, fileInputAccept, mediaKindsIn } from './constants';
import type { PickedFile } from './olaDetect';
import type { MediaKind } from '../../constants/assetTypes';

/**
 * Drag-and-drop target that also opens a file picker.
 *
 * Hand-rolled rather than pulled in: the whole job is three DOM events and a
 * hidden input, and this repo has no drop-zone dependency to match.
 *
 * Both routes end in the same `onFiles` callback, so validation and the queue
 * only ever see one path. The drop target keeps a depth counter because
 * `dragleave` fires when the pointer crosses onto a *child* element too, and
 * counting entries against leaves is what stops the highlight flickering as the
 * pointer moves over the icon and the text inside.
 */

interface AssetDropzoneProps {
  /**
   * Every file with the path it had inside whatever was dropped or picked.
   *
   * The path is what makes OLA banners possible: an ad's `index.html` asks for
   * the literal string `assets/t_bg1.jpg`, so the folder structure has to
   * survive from the picker all the way into the bucket. The ordinary upload
   * flow ignores it and reads `file.name`.
   */
  onFiles: (files: PickedFile[]) => void;
  disabled?: boolean;
  /**
   * OLA mode: offer a folder picker and describe banners rather than files.
   *
   * The drop path already reads directories, so this only changes the picker
   * and the wording — a dragged folder works in either mode.
   */
  folderMode?: boolean;
  /**
   * The kind being uploaded. Narrows the file picker's `accept` and the
   * caption to that kind's types through the same `allowedTypesFor` the modal
   * validates with, so the picker, the caption and the rejection reason can
   * only disagree by changing that one function. Absent means every image and
   * video type.
   */
  mediaKind?: MediaKind;
  /**
   * Carousel mode: offer both pickers. A folder is one carousel (or a parent
   * of several), and loose images make one, so neither picker alone covers it.
   */
  carouselMode?: boolean;
  /**
   * Upload with JSON: the picker also offers `.json` files, the metadata
   * dropped beside each image. A drag-and-drop is never filtered either way.
   */
  withJson?: boolean;
}

/**
 * Read every file under a dropped directory.
 *
 * `dataTransfer.files` reports a dropped folder as a single entry with no
 * extension and `type: ''`, which validation can only reject — so dragging a
 * shoot folder in used to produce one "not a supported image" complaint and an
 * empty queue. `webkitGetAsEntry` is the only way to see inside; it is
 * non-standard in name only and is implemented everywhere this app runs.
 *
 * `readEntries` yields at most 100 children per call and signals the end with
 * an empty batch, so the loop is required, not defensive.
 */
async function readDirectory(reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> {
  const entries: FileSystemEntry[] = [];
  for (;;) {
    const batch = await new Promise<FileSystemEntry[]>((resolve, reject) =>
      reader.readEntries(resolve, reject),
    );
    if (batch.length === 0) return entries;
    entries.push(...batch);
  }
}

async function collectFiles(entry: FileSystemEntry, out: PickedFile[]): Promise<void> {
  if (entry.isFile) {
    const file = await new Promise<File>((resolve, reject) =>
      (entry as FileSystemFileEntry).file(resolve, reject),
    );
    // `fullPath` is the path inside the drop, leading slash and all; stripped
    // so it matches the shape `webkitRelativePath` gives the picker branch.
    out.push({ file, path: (entry.fullPath || `/${file.name}`).replace(/^\/+/, '') });
    return;
  }

  if (entry.isDirectory) {
    const children = await readDirectory((entry as FileSystemDirectoryEntry).createReader());
    // Sequential rather than `Promise.all`: a deep tree would otherwise open a
    // file handle per leaf at once, and the whole thing is bounded by the 500
    // -file cap validation applies straight after.
    for (const child of children) {
      await collectFiles(child, out);
    }
  }
}

/** What the plain `<input type="file">` branch knows about each file's origin. */
function pickedFromInput(files: FileList | null): PickedFile[] {
  return Array.from(files ?? []).map((file) => ({
    // Set only when the input has `webkitdirectory`; the flat picker gives ''.
    path: file.webkitRelativePath || file.name,
    file,
  }));
}

export function AssetDropzone({
  onFiles,
  disabled = false,
  folderMode = false,
  mediaKind,
  carouselMode = false,
  withJson = false,
}: AssetDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const depth = useRef(0);
  const [isOver, setIsOver] = useState(false);
  const allowed = mediaKind === undefined ? ALLOWED_MEDIA_TYPES : allowedTypesFor(mediaKind);
  const accept = withJson
    ? `${fileInputAccept(allowed)},application/json,.json`
    : fileInputAccept(allowed);
  const { images, videos } = mediaKindsIn(allowed);
  const mediaLabel = images && videos ? 'images and videos' : videos ? 'videos' : 'images';
  const formatLabels = [...new Set(Object.values(allowed))].map((extension) =>
    extension.slice(1).toUpperCase(),
  );

  const openPicker = useCallback(() => {
    if (disabled) return;
    (folderMode ? folderInputRef : inputRef).current?.click();
  }, [disabled, folderMode]);

  const openFolderPicker = useCallback(() => {
    if (disabled) return;
    folderInputRef.current?.click();
  }, [disabled]);

  const handleDragEnter = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      if (disabled) return;
      depth.current += 1;
      setIsOver(true);
    },
    [disabled],
  );

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    depth.current -= 1;
    if (depth.current <= 0) {
      depth.current = 0;
      setIsOver(false);
    }
  }, []);

  // Required: without preventDefault on dragover the browser treats the drop as
  // a navigation and opens the image in the tab, losing whatever was queued.
  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
  }, []);

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      depth.current = 0;
      setIsOver(false);
      if (disabled) return;

      // Read synchronously, before any await: `dataTransfer` is emptied once
      // the handler returns, so the entries have to be grabbed up front even
      // though walking them is async.
      const entries = Array.from(event.dataTransfer.items)
        .map((item) => item.webkitGetAsEntry?.() ?? null)
        .filter((entry): entry is FileSystemEntry => entry !== null);

      const flat = pickedFromInput(event.dataTransfer.files);

      if (entries.length === 0) {
        onFiles(flat);
        return;
      }

      void (async () => {
        const collected: PickedFile[] = [];
        try {
          for (const entry of entries) {
            await collectFiles(entry, collected);
          }
        } catch {
          // A directory we could not read is not worth failing the whole drop
          // over — fall back to whatever the flat list gave us.
          onFiles(flat);
          return;
        }
        onFiles(collected);
      })();
    },
    [disabled, onFiles],
  );

  return (
    <div
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      // The whole area opens the picker, not just the "browse" word — the
      // standard dropzone affordance. Left a div rather than made a button:
      // the inner browse button stays as the keyboard/tab access point, and a
      // button may not contain a button.
      onClick={openPicker}
      // The whole area is the control now, so the hover feedback lives on the
      // area (a slightly darker wash) rather than on the "browse" word.
      className={`rounded-lg border border-dashed p-6 text-center transition-colors ${
        isOver
          ? 'border-[#0014cc] bg-[#0014cc]/5'
          : `border-slate-300 bg-slate-50/60${disabled ? '' : ' hover:bg-slate-200/70'}`
      } ${disabled ? 'opacity-60' : 'cursor-pointer'}`}
    >
      <Upload className="mx-auto h-5 w-5 text-slate-400" strokeWidth={2} />
      {carouselMode ? (
        <>
          <p className="mt-2 text-[12.5px] text-slate-600">
            Drag carousel folders or images here, or{' '}
            <button
              type="button"
              disabled={disabled}
              onClick={(event) => {
                event.stopPropagation();
                openFolderPicker();
              }}
              className="font-medium text-[#0014cc] disabled:cursor-not-allowed"
            >
              choose a folder
            </button>{' '}
            /{' '}
            <button
              type="button"
              disabled={disabled}
              onClick={(event) => {
                event.stopPropagation();
                openPicker();
              }}
              className="font-medium text-[#0014cc] disabled:cursor-not-allowed"
            >
              browse images
            </button>
          </p>
          <p className="mt-1 text-[11px] text-slate-400">
            One folder per carousel, 2–6 {formatLabels.join(', ')} images each. A folder of carousel
            folders works too; loose images make one carousel.
          </p>
        </>
      ) : (
        <>
          <p className="mt-2 text-[12.5px] text-slate-600">
            {folderMode ? 'Drag OLA folders here, or ' : `Drag ${mediaLabel} here, or `}
            <button
              type="button"
              disabled={disabled}
              onClick={(event) => {
                // The container opens the picker too; without this a click on
                // "browse" would bubble and ask for it twice.
                event.stopPropagation();
                openPicker();
              }}
              // Deliberately styled like the surrounding sentence: the clickable
              // surface is the whole area, so the word no longer advertises
              // itself. It stays a real button purely as the keyboard/tab access
              // point, which focus-visible still outlines.
              className="disabled:cursor-not-allowed"
            >
              {folderMode ? 'choose a folder' : 'browse'}
            </button>
          </p>
          <p className="mt-1 text-[11px] text-slate-400">
            {folderMode
              ? 'One folder per OLA, each with an index.html and a _backup.jpg'
              : withJson
                ? `${formatLabels.join(', ')}, each with a JSON file of the same name`
                : `${formatLabels.join(', ')} · up to 1 GB each`}
          </p>
        </>
      )}

      {/* Two inputs rather than one with a toggled attribute: Chrome caches the
          picker mode from the first `.click()`, so flipping `webkitdirectory`
          on a live input opens the wrong dialog until the page reloads. */}
      <input
        ref={folderInputRef}
        data-testid="folder-input"
        type="file"
        // Spread because `webkitdirectory` is not in React's InputHTMLAttributes;
        // its presence is what switches the picker into folder mode.
        {...{ webkitdirectory: '' }}
        multiple
        className="hidden"
        onClick={(event) => event.stopPropagation()}
        onChange={(event) => {
          onFiles(pickedFromInput(event.target.files));
          event.target.value = '';
        }}
      />

      <input
        ref={inputRef}
        data-testid="file-input"
        type="file"
        multiple
        accept={accept}
        className="hidden"
        // The input sits inside the clickable container, and `.click()` on it
        // fires a click event that bubbles — without this it would re-enter
        // the container's handler and ask for the picker again.
        onClick={(event) => event.stopPropagation()}
        onChange={(event) => {
          onFiles(pickedFromInput(event.target.files));
          // Cleared so picking the same file twice in a row still fires
          // `change` — the browser suppresses it when the value is unchanged.
          event.target.value = '';
        }}
      />
    </div>
  );
}
